import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Inbox, FolderKanban, AlertTriangle, ShieldAlert, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CaseCard, StatCard, SectionTitle, EmptyState } from "@/components/CaseCard";
import { StatusChip } from "@/components/StatusChip";
import { Skeleton } from "@/components/ui/skeleton";

const QUEUE_META = {
  entry_request: { label: "Entry request", tone: "amber" },
  plan: { label: "Plan approval", tone: "amber" },
  decision: { label: "Decision sign-off", tone: "red" },
};

export default function OpsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
    refetchInterval: 30000,
  });

  if (isLoading || !data) {
    return <div className="grid gap-4 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;
  }

  const s = data.stats;
  const isLeadership = user.role === "leadership";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-patria-ink">
          {isLeadership ? "Leadership overview" : "People Operations"}
        </h1>
        <p className="text-patria-muted mt-1">
          {isLeadership ? "Org-wide COMPASS activity, approvals, and separations." : "Approve entries and plans, monitor check-ins across all campuses."}
        </p>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Awaiting my approval" value={s.pending_approvals || 0} icon={Inbox} tone={s.pending_approvals ? "amber" : "neutral"} testId="stat-pending" />
        <StatCard label="Active cases" value={s.active} icon={FolderKanban} tone="green" testId="stat-active" />
        <StatCard label="Overdue" value={s.overdue} icon={AlertTriangle} tone={s.overdue ? "red" : "neutral"} testId="stat-overdue" />
        <StatCard label="Closed" value={s.closed} icon={FolderKanban} tone="neutral" testId="stat-closed" />
      </div>

      {/* Approval queue */}
      <div>
        <SectionTitle>Approval queue</SectionTitle>
        {data.approval_queue?.length === 0 ? (
          <EmptyState title="Queue is clear" hint="New entry requests and plans will appear here for review." testId="empty-queue" />
        ) : (
          <div className="space-y-2" data-testid="approval-queue">
            {data.approval_queue.map((q) => {
              const qm = QUEUE_META[q.queue_type];
              return (
                <button
                  key={q.id + q.queue_type}
                  onClick={() => navigate(`/cases/${q.id}`)}
                  data-testid={`queue-item-${q.id}`}
                  className="w-full flex items-center justify-between gap-3 rounded-2xl bg-patria-surface border border-patria-muted/15 px-4 py-3.5 hover:border-patria-terracotta/40 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusChip label={qm.label} tone={qm.tone} />
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-medium text-patria-ink truncate">{q.employee_name}</p>
                      <p className="text-xs text-patria-muted truncate">{q.campus} · {q.manager_name}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm text-patria-terracottaDeep font-medium shrink-0">Review <ArrowRight className="h-4 w-4" /></span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Separations (leadership) */}
      {isLeadership && (
        <div>
          <SectionTitle>Separation sign-offs</SectionTitle>
          {data.separation_approvals?.length === 0 ? (
            <EmptyState title="No separations pending" hint="Separation outcomes require your sign-off and will appear here." testId="empty-separations" />
          ) : (
            <div className="space-y-2" data-testid="separation-queue">
              {data.separation_approvals.map((q) => (
                <button
                  key={q.id}
                  onClick={() => navigate(`/cases/${q.id}`)}
                  data-testid={`separation-item-${q.id}`}
                  className="w-full flex items-center justify-between gap-3 rounded-2xl bg-patria-terracottaDeep/5 border border-patria-terracottaDeep/30 px-4 py-3.5 hover:bg-patria-terracottaDeep/10 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <ShieldAlert className="h-5 w-5 text-patria-terracottaDeep shrink-0" />
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-medium text-patria-ink truncate">{q.employee_name}</p>
                      <p className="text-xs text-patria-muted truncate">{q.campus} · {q.manager_name}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm text-patria-terracottaDeep font-medium shrink-0">Sign off <ArrowRight className="h-4 w-4" /></span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Overdue / at-risk */}
      <div>
        <SectionTitle action={<Button variant="ghost" size="sm" className="text-patria-green" onClick={() => navigate("/cases")} data-testid="view-all-cases">All cases</Button>}>
          Overdue & at-risk
        </SectionTitle>
        {data.overdue_cases?.length === 0 ? (
          <EmptyState title="Everything on track" hint="No overdue check-ins or past-due reviews." testId="empty-overdue" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.overdue_cases.map((c) => <CaseCard key={c.id} c={c} showManager />)}
          </div>
        )}
      </div>
    </div>
  );
}
